import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import dma from '../../data/google-dma-topojson.json';

// colors
const root = document.documentElement;
const primaryColor = getComputedStyle(root)
  .getPropertyValue('--primary')
  .trim();
const fadedPrimary = `rgba(${hexToRgb(primaryColor)}, 0.5)`; // <-- faded version

function hexToRgb(hex) {
  // Converts "#rrggbb" → "r, g, b"
  const cleanHex = hex.replace('#', '');
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}

const DmaSelectorMap = ({ selectedDma, setSelectedDma }) => {
  const mapRef = useRef(null);
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const transformRef = useRef(d3.zoomIdentity);

  useEffect(() => {
    if (!dma) return;

    const container = mapRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const svg = d3
      .select(container)
      .attr('width', width)
      .attr('height', height);

    svg.selectAll('*').remove(); // Clean slate

    svgRef.current = svg;

    const g = svg.append('g');

    const projection = d3
      .geoMercator()
      .fitSize([width, height], topojson.feature(dma, dma.objects.dma_shp));

    const path = d3.geoPath().projection(projection);

    const nielsen = topojson.feature(dma, dma.objects.dma_shp).features;

    const tooltip = container.parentNode.querySelector('#dma-tooltip');

    g.selectAll('path')
      .data(nielsen)
      .enter()
      .append('path')
      .attr('d', path)
      .attr('id', (d) =>
        d.properties.dma_name === 'Alaska'
          ? 'national'
          : `dma-${d.properties.dma_name}`
      )
      .attr('fill', fadedPrimary)
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5)
      .attr('cursor', 'pointer')
      .classed('selectedDMA', (d) => d.properties.dma_name === selectedDma)
      .on('click', (event, d) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'mediaScopeDMAMapSelect',
          dma: d.properties.dma_name
        });
        g.selectAll('path.selectedDMA').classed('selectedDMA', false);
        d3.select(event.target).classed('selectedDMA', true);
        setSelectedDma(d.properties.dma_name);
        const dataSection = document.getElementById('dma-data-section');
        if (dataSection) {
          const vhOffset = window.innerHeight * 0.3; // scroll so it's 30% from top
          const y =
            dataSection.getBoundingClientRect().top + window.pageYOffset - vhOffset;

          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      })
      .on('mouseover', function (event, d) {
        tooltip.style.opacity = 1;
        tooltip.innerHTML = d.properties.dma_name;
      })
      .on('mousemove', function (event) {
        const tooltipOffset = 25;
        const bounds = container.getBoundingClientRect();

        const x = event.clientX - bounds.left + tooltipOffset;
        const y = event.clientY - bounds.top + tooltipOffset;

        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
      })
      .on('mouseout', function () {
        tooltip.style.opacity = 0;
      });

    // Zoom behavior
    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 2])
      .filter(
        (event) => event.type === 'mousedown' || event.type === 'touchstart'
      )
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        g.attr('transform', event.transform);
      });

    zoomRef.current = zoom;
    svg.call(zoom);
  }, [selectedDma]);

  const handleZoom = (direction) => {
    const svg = svgRef.current;
    const zoom = zoomRef.current;
    const { width, height } = svgRef.current.node().getBoundingClientRect();
    const center = [width / 2, height / 2];

    const scaleFactor = direction === 'in' ? 1.2 : 0.8;
    const newTransform = transformRef.current
      .translate(center[0], center[1])
      .scale(scaleFactor)
      .translate(-center[0], -center[1]);

    svg.transition().duration(300).call(zoom.transform, newTransform);
  };

  return (
    <div className="dma-selector-map--container">
      <svg ref={mapRef} className="ms-map"></svg>
      <div
        className="dma-tooltip"
        id="dma-tooltip"
        style={{ position: 'absolute', opacity: 0 }}
      ></div>
      <div className="ms-map--zoom-controls">
        <button onClick={() => handleZoom('in')}>+</button>
        <button onClick={() => handleZoom('out')}>−</button>
      </div>
    </div>
  );
};

export default DmaSelectorMap;
